/**
 * design.md §4 `reference`: a link to a person or place with a phone number carries it,
 * one tap from a call — under the picker as soon as a contact is chosen (and again
 * when another is), and beside the name on a record's page. Every phone field of the
 * linked table, filled ones only.
 */

import { useQuery } from '@tanstack/react-query'
import { Phone } from 'lucide-react'

import { api } from '@/lib/api'
import type { TableDef } from '@/lib/meta'
import { useTable } from '@/lib/meta'

/** "tel:" wants digits and a leading +; people type spaces and dashes. */
export function telHref(value: string): string {
  return `tel:${value.replace(/[^\d+]/g, '')}`
}

export function LinkedPhones({
  table,
  record,
  className,
}: {
  table: TableDef | undefined
  record: Record<string, unknown> | undefined
  className?: string
}) {
  const phones = (table?.fields ?? [])
    .filter((f) => f.type === 'phone' && f.status === 'active')
    .map((f) => ({ field: f, value: record?.[f.slug] }))
    .filter((p): p is { field: typeof p.field; value: string } => typeof p.value === 'string' && p.value.trim() !== '')
  if (phones.length === 0) return null
  return (
    <span className={className ?? 'flex flex-wrap items-center gap-x-3 gap-y-1'}>
      {phones.map(({ field, value }) => (
        <a
          key={field.slug}
          href={telHref(value)}
          onClick={(event) => event.stopPropagation()}
          className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
          aria-label={`Call ${value} (${field.label})`}
          title={`Call · ${field.label}`}
        >
          <Phone className="size-3" aria-hidden />
          <span className="tabular">{value}</span>
        </a>
      ))}
    </span>
  )
}

/** The phones of the record a link points at, fetched by id: for a record's page. */
export function LinkedPhonesById({ reference, id }: { reference: string; id: string }) {
  const meta = useTable(reference)
  const hasPhone = (meta.data?.fields ?? []).some((f) => f.type === 'phone' && f.status === 'active')
  const [app, table] = reference.split('.')
  const linked = useQuery({
    queryKey: ['record', reference, id],
    queryFn: () => api.get<Record<string, unknown>>(`/api/data/${app}/${table}/${id}`),
    enabled: hasPhone && Boolean(app && table && id),
    staleTime: 60_000,
  })
  return <LinkedPhones table={meta.data} record={linked.data} />
}
